import type { AssignableAdminUser } from '../../../../domain/src/lib/repository/file-assignment.model';
import { normalizeTenantId } from '../../../../data-access/src/lib/shared/tenant-context.util';
import { emailIsVaultSystemSuperadmin } from '@nebula/shared-vault/data-access';

const ASSIGN_ROLE_LABELS: Record<string, string> = {
  consulta: 'Consulta',
  admin_empresa: 'Administrador',
  rh: 'Recursos Humanos',
  archivista: 'Archivista',
  auditor: 'Auditor',
};

export interface MapUsersForAssignDialogOptions {
  superAdminFallbackAll: boolean;
  apiAlreadyTenantScoped?: boolean;
}

export function formatAssignRoleLabel(role: string): string {
  const key = (role || '').trim().toLowerCase();
  return ASSIGN_ROLE_LABELS[key] ?? role;
}

function tenantScopeMatches(linkTenantId: string, scope: string): boolean {
  const link = (linkTenantId || '').trim();
  const scoped = (scope || '').trim();
  if (!link || !scoped) return false;
  if (link.toLowerCase() === scoped.toLowerCase()) return true;
  const normalizedLink = normalizeTenantId(link);
  const normalizedScope = normalizeTenantId(scoped);
  return !!(normalizedLink && normalizedScope && normalizedLink === normalizedScope);
}

function resolveAssignUserRoleInTenant(user: AssignableAdminUser, scopeTenantId: string): string {
  const scope = scopeTenantId.trim();
  const tenants = user.tenants ?? [];

  if (scope) {
    const scoped = tenants.find((t) => tenantScopeMatches(t.tenant_id || '', scope));
    const scopedRole = (scoped?.role || '').trim();
    if (scopedRole) return scopedRole;
  }

  if (tenants.length === 1) {
    const singleRole = (tenants[0]?.role || '').trim();
    if (singleRole) return singleRole;
  }

  for (const link of tenants) {
    const role = (link.role || '').trim();
    if (role) return role;
  }

  return '';
}

function enrichUserForTenant(user: AssignableAdminUser, scopeTenantId: string): AssignableAdminUser {
  return {
    ...user,
    role_in_tenant: resolveAssignUserRoleInTenant(user, scopeTenantId),
  };
}

function isAdminEmpresaRole(role: string): boolean {
  const key = (role || '').trim().toLowerCase();
  return key === 'admin_empresa' || key === 'administrador';
}

function isSuperadminRole(role: string): boolean {
  const key = (role || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
  return key === 'superadmin' || key === 'superadministrador';
}

/** Superadmin por flag, email canónico/replicación, rol resuelto o rol embebido en tenant. */
function userIsSuperadmin(user: AssignableAdminUser, scopeTenantId: string): boolean {
  if (user.is_superadmin) return true;
  if (emailIsVaultSystemSuperadmin(user.email)) return true;
  if (isSuperadminRole(resolveAssignUserRoleInTenant(user, scopeTenantId))) return true;
  return (user.tenants ?? []).some((t) => isSuperadminRole(t.role || ''));
}

/**
 * Activos, excepto superadmin y administradores de empresa:
 * ambos ya tienen acceso a todo el repositorio, no se asignan por carpeta.
 * Incluye qa@/dev@ aunque el API no mande is_superadmin ni rol.
 */
function isEligibleAssignTarget(user: AssignableAdminUser, scopeTenantId: string): boolean {
  if (user.is_active === false) return false;
  if (userIsSuperadmin(user, scopeTenantId)) return false;
  if (isAdminEmpresaRole(resolveAssignUserRoleInTenant(user, scopeTenantId))) return false;
  return true;
}

function userBelongsToTenantScope(user: AssignableAdminUser, scope: string): boolean {
  const tenants = user.tenants ?? [];
  if (tenants.length === 0) {
    return true;
  }
  return tenants.some((t) => tenantScopeMatches(t.tenant_id || '', scope));
}

function applyAssignTargetFilters(
  users: AssignableAdminUser[],
  scopeTenantId: string,
): AssignableAdminUser[] {
  return users
    .filter((u) => isEligibleAssignTarget(u, scopeTenantId))
    .map((u) => enrichUserForTenant(u, scopeTenantId));
}

/** Usuarios elegibles para asignar en Bóveda Digital. */
export function mapUsersForAssignDialog(
  users: AssignableAdminUser[],
  scopeTenantId: string,
  options: MapUsersForAssignDialogOptions,
): AssignableAdminUser[] {
  const scope = scopeTenantId.trim();
  const eligible = users.filter((u) => isEligibleAssignTarget(u, scope));

  if (options.apiAlreadyTenantScoped) {
    return applyAssignTargetFilters(eligible, scope);
  }

  let list = eligible.map((u) => enrichUserForTenant(u, scope));

  if (scope) {
    const inTenant = list.filter((u) => userBelongsToTenantScope(u, scope));
    if (inTenant.length > 0) {
      list = inTenant.map((u) => enrichUserForTenant(u, scope));
    } else if (eligible.length > 0) {
      list = eligible.map((u) => enrichUserForTenant(u, scope));
    } else {
      list = [];
    }
  }

  return list.filter((u) => isEligibleAssignTarget(u, scope));
}

export function assignUserCheckboxLabel(user: AssignableAdminUser): string {
  const name = (user.full_name || user.email || user.id).trim();
  const email = (user.email || '').trim();
  const role = (user.role_in_tenant || '').trim();
  const rolePart = role ? ` — ${formatAssignRoleLabel(role)}` : '';
  if (email && name !== email) {
    return `${name} (${email})${rolePart}`;
  }
  return `${name}${rolePart}`;
}
